import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlatList, View, ActivityIndicator, Image } from 'react-native';
import { RouteProp, useFocusEffect, useIsFocused, useNavigationState, useRoute } from '@react-navigation/native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { accessService } from '../../../services/central-api/access';
import Divider from '../../../components/Divider';
import Badge from '../../../components/Badge';
import ChatBubble from '../../../components/chat/ChatBubble';
import { translate } from '../../../services/translate';
import { AccessParamList } from '../../../typings/Params';

interface SupportTicketDetailParams {
    navigation: Navigation;
}

type DateBadge = {
    type: 'date-badge';
    date: string;
    id: string;
};

type CommentOrBadge = SupportTicketComment | DateBadge;

export default function SupportTicketDetail({ navigation }: SupportTicketDetailParams) {
    const route = useRoute<RouteProp<AccessParamList, 'SupportTicketDetail'>>();
    const ticketId = route.params.id;

    const { styles, theme } = useStyles(styleSheet);
    const index = useNavigationState(state => state);
    const { user: authenticatedUser } = useAuth();
    const isFocused = useIsFocused();
    const [ticket, setTicket] = useState<SupportTicket>();
    const [comments, setComments] = useState<SupportTicketComment[]>();
    const [users, setUsers] = useState<{ [userId: string]: SupportTicketUser | undefined; }>({});
    const flatListRef = useRef<FlatList<CommentOrBadge>>(null);


    const otherUser = Object.values(users).find(u => u?.role === 'end-user');

    const ticketDetailQuery = useQuery({
        queryKey: ['TicketDetailComponent', ticketId],
        queryFn: () => accessService.getSupportTicket(ticketId!),
        enabled: !!ticketId && !!authenticatedUser,
    });

    const ticketsCommentsQuery = useQuery({
        queryKey: ['supportTicketComments', ticketId],
        queryFn: () => accessService.getSupportTicketComments(ticketId!),
        enabled: !!ticketId && !!authenticatedUser,
    });

    useEffect(() => {
        if (!ticketDetailQuery.data) {
            return;
        }
        setTicket(ticketDetailQuery.data);
    }, [ticketDetailQuery.data]);

    async function getUsers(authors: number[]) {
        const newUsers: { [userId: string]: SupportTicketUser; } = {};
        for (const author of authors) {
            if (!(String(author) in users)) {
                const user = await accessService.getTicketUser(ticketId!, author);
                newUsers[String(author)] = user;
                setUsers(prev => ({ ...prev, ...newUsers }));
            }
        }
    }

    useEffect(() => {
        if (!ticketsCommentsQuery.data) {
            return;
        }
        setComments(ticketsCommentsQuery.data.rows);
        const authors = [...new Set(ticketsCommentsQuery.data.rows.map(c => c.authorId))];
        getUsers(authors);
    }, [ticketsCommentsQuery.data]);

    useFocusEffect(useCallback(() => {
        if (isFocused && ticketId && authenticatedUser) {
            ticketsCommentsQuery.refetch();
            ticketDetailQuery.refetch();
        }
    }, [isFocused, ticketId, authenticatedUser]));

    function handleScrollToEnd(_width: number, height: number) {
        if (flatListRef.current) {
            flatListRef.current.scrollToOffset({ offset: height });
        }
    }

    function getCommentsWithDateBadges(): CommentOrBadge[] {
        if (!comments || comments.length === 0) {
            return [];
        }
        const result: CommentOrBadge[] = [];
        let lastDate: string | null = null;

        comments.forEach((comment, idx) => {
            const commentDate = moment(comment.createdAt).format('YYYY-MM-DD');
            if (lastDate !== commentDate) {
                result.push({
                    type: 'date-badge',
                    date: commentDate,
                    id: `badge-${commentDate}-${idx}`,
                });
                lastDate = commentDate;
            }
            result.push(comment);
        });

        return result;
    }

    function renderComment({ item, index: itemIndex }: { item: CommentOrBadge; index: number; }) {
        if ('type' in item && item.type === 'date-badge') {
            const today = moment().format('YYYY-MM-DD');
            const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');

            let dateText = moment(item.date).format('DD/MM/YYYY');
            if (item.date === today) {
                dateText = translate('today');
            } else if (item.date === yesterday) {
                dateText = translate('yesterday');
            }

            return (
                <View style={styles.dateBadge}>
                    <Badge
                        backgroundColor={theme.colors.chat.badgeBackground}
                        textColor={theme.colors.chat.badgeTextColor}
                        style={{ borderColor: theme.colors.neutral[200], borderWidth: 1 }}
                        mode='ROUNDED'
                    >
                        {dateText}
                    </Badge>
                </View>
            );
        }

        const comment = item as SupportTicketComment;
        const user = users[String(comment.authorId)];
        const isSupport = user?.role !== 'end-user';

        const commentsWithBadges = getCommentsWithDateBadges();
        const previousItem = itemIndex > 0 ? commentsWithBadges[itemIndex - 1] : null;
        let previousIsSupport: boolean | null = null;

        if (previousItem && !('type' in previousItem)) {
            const previousComment = previousItem as SupportTicketComment;
            const previousUser = users[String(previousComment.authorId)];
            previousIsSupport = previousUser?.role !== 'end-user';
        }

        const marginTop = previousIsSupport === null || previousIsSupport === isSupport ? 8 : 16;

        return (
            <View style={{
                width: '100%',
                justifyContent: 'flex-start',
                marginTop,
            }}>
                <ChatBubble
                    comment={comment}
                    user={user}
                    isSupport={isSupport}
                    isOwnMessage={!isSupport} // end-user recebe fundo amarelo
                    alignRight={false}
                />
            </View>
        );
    }

    function getItemKey(item: CommentOrBadge): string {
        if ('type' in item && item.type === 'date-badge') {
            return item.id;
        }
        return String((item as SupportTicketComment).id);
    }

    if (!ticket) {
        return <></>;
    }
    return (
        <Container>
            <Header
                title={`#${ticket.id} - ${ticket.subject}`}
                goBackAction={() => {
                    if (index.routes.length > 1) {
                        return navigation.goBack();
                    } else {
                        window.location.href = '/access/support-tickets';
                    }
                }}
            />
            <Card style={{ padding: 0 }}>
                {ticket && (ticketsCommentsQuery.isLoading || ticketDetailQuery.isLoading) ? (
                    <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                ) : (
                    <>
                        <View>
                            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', padding: 16 }}>
                                <View style={styles.userAvatar}>
                                    {otherUser ? (
                                        <MyAppText style={{ color: theme.colors.white[500], fontSize: 16, fontWeight: 'bold' }}>
                                            {otherUser.name[0].toUpperCase()}
                                        </MyAppText>
                                    ) : (
                                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', padding: 16 }}>
                                            <Image style={styles.userFrame} source={require('../../../../assets/SentinelX_chat_dark.png')} />
                                            <MyAppText style={{ color: theme.colors.chat.sentinelAvatar, fontSize: 16, fontWeight: '700' }}>{otherUser}</MyAppText>
                                        </View>
                                    )}
                                </View>
                                <MyAppText style={{ color: theme.colors.chat.userAvatar, fontSize: 16, fontWeight: '700' }}>
                                    {otherUser?.name}
                                </MyAppText>
                            </View>
                            <Divider color={theme.colors.neutral['200']} style={{ zIndex: -1 }} />
                        </View>

                        <View style={{ flex: 1, backgroundColor: theme.colors.chat.background, paddingHorizontal: 16, paddingVertical: 24 }}>
                            <View style={styles.openBadge}>
                                <Badge
                                    backgroundColor={theme.colors.chat.badgeBackground}
                                    textColor={theme.colors.chat.badgeTextColor}
                                    style={{ borderColor: theme.colors.neutral[200], borderWidth: 1 }}
                                    mode='ROUNDED'
                                >
                                    <MyAppText>{translate('ticketOpenAt')}: {moment(ticketDetailQuery.data?.createdAt).format('DD/MM/YYYY')}</MyAppText>
                                </Badge>
                            </View>

                            <View style={{ flex: 1 }}>
                                <FlatList
                                    ref={flatListRef}
                                    data={getCommentsWithDateBadges()}
                                    renderItem={renderComment}
                                    keyExtractor={getItemKey}
                                    contentContainerStyle={{
                                        width: '100%',
                                        alignItems: 'stretch',
                                    }}
                                    onContentSizeChange={handleScrollToEnd}
                                    style={{ flex: 1 }}
                                />
                            </View>
                        </View>
                    </>
                )}
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    userFrame: {
        width: 40,
        aspectRatio: 1,
        borderRadius: 50,
        backgroundColor: theme.colors.neutral[300],
        justifyContent: 'center',
        alignItems: 'center',
    },
    userAvatar: {
        width: 40,
        height: 40,
        borderRadius: 50,
        backgroundColor: theme.colors.chat.userAvatar,
        justifyContent: 'center',
        alignItems: 'center',
    },
    dateBadge: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: 16,
    },
    openBadge: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    }
}));
